import { useMemo } from 'react';
import { type Rules } from '@Types';
import { convertValue } from '@innovyze/lib_asset_schema';

interface ConvertedRuleValues {
	value1: number | string | string[] | null | undefined;
	value2: number | string | string[] | null | undefined;
	unitLength: number | null | undefined;
}

/**
 * Hook to convert rule values from stored units to display units
 *
 * When loading old queries that have values stored in millimeters (rule.unit = 'mm'),
 * but the new system uses meters (fieldUnit = 'm'), this hook converts the values
 * so they display correctly.
 *
 * @param initialRuleValues - The rule values from the stored query
 * @param fieldUnit - The current display unit from lib-asset-schema
 * @returns Converted values ready for display
 *
 * @example
 * // Old query: { value1: 100, unit: 'mm' }
 * // New fieldUnit: 'm'
 * // Result: { value1: 0.1, ... }
 */
export const useConvertedRuleValues = (
	initialRuleValues: Rules,
	fieldUnit: string,
): ConvertedRuleValues =>
	useMemo(() => {
		const storedUnit = initialRuleValues.unit;
		const needsConversion =
			storedUnit && fieldUnit && storedUnit !== fieldUnit;

		// Helper to safely convert numeric values
		const convertIfNeeded = (
			value: number | string | string[] | null | undefined,
		): number | string | string[] | null | undefined => {
			// Skip conversion if not needed
			if (!needsConversion) {
				return value;
			}

			// Skip conversion for arrays (like defect codes)
			if (Array.isArray(value)) {
				return value;
			}

			// Skip conversion for null/undefined
			if (value === null || value === undefined) {
				return value;
			}

			// Convert string numbers to actual numbers for conversion
			const numericValue =
				typeof value === 'string' ? parseFloat(value) : value;

			// If it's not a valid number, return original
			if (typeof numericValue !== 'number' || isNaN(numericValue)) {
				return value;
			}

			return convertValue(numericValue, storedUnit, fieldUnit);
		};

		return {
			value1: convertIfNeeded(initialRuleValues.value1),
			value2: convertIfNeeded(initialRuleValues.value2),
			unitLength: convertIfNeeded(initialRuleValues.unitLength) as
				number | null | undefined,
		};
	}, [initialRuleValues, fieldUnit]);
